// 활동 로그 패널 — 시드 시스템 로그 + 감사 로그(로그인/관리행위) 통합 표시 + CSV 내보내기
import { useState } from 'react';
import { ACTIVITY_LOG } from '../lib/seedData';
import { downloadCsv, toCsv } from '../lib/csv';
import { renderSafeHtml, stripHtml } from '../lib/sanitize';
import { readAudit, type AuditEntry } from '../lib/auth';
import { useScanStore } from '../lib/scanStore';

type View = 'system' | 'audit';

export function Activity() {
  const [view, setView] = useState<View>('system');
  const audit = readAudit();
  const liveActivity = useScanStore((s) => s.activityEntries);
  // 실시간 항목을 최상단에, 시드 데이터를 하단에
  const systemLog = [...liveActivity, ...ACTIVITY_LOG];

  function exportCsv() {
    if (view === 'system') {
      const csv = toCsv(
        ['time', 'type', 'message'],
        systemLog.map((a) => ({ time: a.time, type: a.type, message: stripHtml(a.html) })),
      );
      downloadCsv(`fileguard-activity-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    } else {
      const csv = toCsv(
        ['timestamp', 'kind', 'detail'],
        audit.map((e) => ({ timestamp: new Date(e.ts).toISOString(), kind: e.kind, detail: e.detail })),
      );
      downloadCsv(`fileguard-audit-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    }
  }

  return (
    <>
      <div className="sect-head">
        <div className="sect-title">📡 활동 로그 <em>실시간 시스템 활동 + 보안 감사 로그</em></div>
        <div className="flex-row">
          <button
            className={`btn btn-sm ${view === 'system' ? 'btn-pri' : 'btn-ghost'}`}
            onClick={() => setView('system')}
          >
            📋 시스템 ({systemLog.length})
          </button>
          <button
            className={`btn btn-sm ${view === 'audit' ? 'btn-pri' : 'btn-ghost'}`}
            onClick={() => setView('audit')}
          >
            🔐 감사 로그 ({audit.length})
          </button>
          <button className="btn btn-ghost btn-sm" onClick={exportCsv}>📥 로그 내보내기</button>
        </div>
      </div>
      <div className="tbl-wrap">
        {view === 'system' ? (
          <div>
            {systemLog.map((a, i) => (
              <div key={i} className="log-item">
                <div className="log-time">{a.time}</div>
                <div className={`log-dot ld-${a.type}`} />
                <div className="log-text">{renderSafeHtml(a.html)}</div>
              </div>
            ))}
          </div>
        ) : (
          <AuditView entries={audit} />
        )}
      </div>
    </>
  );
}

function AuditView({ entries }: { entries: AuditEntry[] }) {
  if (entries.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>
        감사 로그가 없습니다. 로그인 또는 관리 작업 수행 시 자동 기록됩니다.
      </div>
    );
  }
  const colorOf = (k: AuditEntry['kind']): 'green' | 'red' | 'yellow' | 'blue' =>
    k === 'login.success' || k === 'logout' ? 'green'
      : k === 'login.failure' || k === 'login.lockout' ? 'red'
        : k === 'session.expired' ? 'yellow'
          : 'blue';
  return (
    <div>
      {entries.map((e, i) => (
        <div key={i} className="log-item">
          <div className="log-time" style={{ minWidth: 140 }}>{new Date(e.ts).toLocaleTimeString()}</div>
          <div className={`log-dot ld-${colorOf(e.kind)}`} />
          <div className="log-text">
            <code style={{ marginRight: 8 }}>{e.kind}</code>
            <span style={{ color: 'var(--text2)' }}>{e.detail}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
